import { useRef } from 'preact/hooks';
import { getPriceType, type URLFilterParams } from 'utils/url.ts';
import { WatchdogFilterBoxItem } from 'components/modal/watchdog/WatchdogFilterBoxItem.tsx';
import { localize } from 'i18n/localize';
import { DEFAULT_RADIUS_VALUE, LOCALITY_CELA_CR_SEO_NAME, PRICE, type TLocality } from 'utils/constants.ts';
import { price as formatPrice } from 'utils/format';
import { categoriesMap } from 'signals/category';
import type { LocalitySuggestionData, ResponseLocalityData, LocalityData } from 'service/locality/model.ts';
import { FilterPrice, type PriceChange, type PriceChangeReason } from 'components/filters/FilterPrice.tsx';
import { FilterLocation } from 'components/filters/FilterLocation.tsx';
import { useSignal } from '@preact/signals';
import type { ModalWatchdogDataFilter } from 'components/modal/watchdog/WatchdogModal.tsx';
import { sendChangeSavedSearchAnalytics } from 'lib/dot/savedSearches/utils.ts';

function getLocalityText(locality: Partial<ResponseLocalityData & TLocality>, radius?: number) {
	if (locality.seoName === LOCALITY_CELA_CR_SEO_NAME) {
		return $t.entireCzechia;
	}
	return (
		(locality.altName ?? locality.name ?? locality.seoName ?? locality.district) +
		(radius ? ` (+${radius} km)` : '')
	);
}

function getPriceText(price: URLFilterParams['price'], agreement?: boolean) {
	let str = '';

	if (price.type === PRICE.FREE) {
		str = $t.filters.free;
	} else if (price.from && price.to) {
		str = `${$t.filters.from} ${formatPrice(price.from)} ${$t.filters.to} ${formatPrice(price.to)}`;
	} else if (price.from) {
		str = `${$t.filters.from} ${formatPrice(price.from)}`;
	} else if (price.to) {
		str = `${$t.filters.to} ${formatPrice(price.to)}`;
	} else {
		str = $t.filters.noLimit;
	}

	if (agreement) {
		str += ` (${$t.filters.includingAgreement})`;
	}

	return str;
}

const $t = localize();

export type Props = {
	filter: ModalWatchdogDataFilter;
	forceDisabled: boolean;
	showDeleteButton?: boolean;
	onDelete: () => void;
	onRadiusInput: (event: Event, value: number) => void;
	onLocationInput: (
		event: Event,
		value: LocalitySuggestionData | undefined,
		resolveGpsLocalityCallback: (localityData: LocalityData) => void,
	) => void;
	onAgreementChanged: (value: boolean) => void;
	onPriceChanged: (values: PriceChange & { changeSource: PriceChangeReason }) => void;
	onPriceSubmit: (values: PriceChange) => void;
};

export function WatchdogFilterBox({
	filter,
	showDeleteButton,
	onDelete,
	forceDisabled,
	onRadiusInput,
	onLocationInput,
	onAgreementChanged,
	onPriceChanged,
	onPriceSubmit,
}: Props) {
	const { price_to, price_from, hide_price_by_agreement, price_for_free } = filter;
	const showPriceEdit = useSignal(false);
	const showLocalityEdit = useSignal(false);
	const radiusDropdownContainerRef = useRef<HTMLDivElement>(null);

	return (
		<>
			<div className="relative bg-light-gray mb-4 px-6 py-4 rounded-2xl flex flex-col gap-2">
				<div>
					<h1 class="font-bold">
						{(filter.category_id &&
							(categoriesMap.value[filter.category_id]?.name ?? $t.categories.unknown)) ||
							$t.categories.allCategories}
					</h1>
				</div>
				{filter.phrase && (
					<div>
						<span class="font-bold">„{filter.phrase}“</span>
					</div>
				)}
				<div>
					{!showLocalityEdit.value && (
						<WatchdogFilterBoxItem
							title={$t.filters.labelLocality}
							value={
								filter.locality
									? getLocalityText(filter.locality, filter.radius_precision)
									: $t.entireCzechia
							}
							onEdit={() => {
								showLocalityEdit.value = true;
								sendChangeSavedSearchAnalytics('locality');
							}}
						/>
					)}
					{showLocalityEdit.value && (
						<div className="mt-2">
							<FilterLocation
								dynamicBackground={false}
								radiusValue={filter.radius_precision ?? DEFAULT_RADIUS_VALUE}
								localityValue={filter.locality}
								onRadiusInput={onRadiusInput}
								onLocationInput={onLocationInput}
								useLocalState={true}
								radiusDropdownContainerPortalRefElement={radiusDropdownContainerRef}
							/>
						</div>
					)}
				</div>
				{!showPriceEdit.value && (
					<WatchdogFilterBoxItem
						title={$t.filters.labelPrice}
						value={getPriceText(
							{
								type: filter.price_for_free
									? PRICE.FREE
									: filter.price_to && filter.price_from
										? PRICE.BETWEEN
										: filter.price_to
											? PRICE.TO
											: PRICE.FROM,
								from: filter.price_from,
								to: filter.price_to,
								seoName: '',
							},
							!filter.hide_price_by_agreement,
						)}
						onEdit={() => {
							showPriceEdit.value = true;
							sendChangeSavedSearchAnalytics('price');
						}}
					/>
				)}
				{showPriceEdit.value && (
					<div className="my-2">
						<FilterPrice
							dynamicBackground={false}
							values={{
								to: price_to,
								from: price_from,
								agreement: !hide_price_by_agreement,
								type: getPriceType(price_to, price_from, !!price_for_free),
							}}
							onAgreementChanged={onAgreementChanged}
							onPriceChanged={onPriceChanged}
							onPriceSubmit={onPriceSubmit}
						/>
					</div>
				)}
				{showDeleteButton && (
					<button className="link text-sm font-medium self-start" onClick={onDelete} disabled={forceDisabled}>
						<span className="hidden md:block">{$t.watchdog.delete}</span>
						<span className="md:hidden">{$t.watchdog.deleteShort}</span>
					</button>
				)}
			</div>
			<div class="md:hidden" ref={radiusDropdownContainerRef} />
		</>
	);
}
